import React, { useEffect, useRef, useState } from 'react';
import {
  Home, Heart, HeartHandshake, Stethoscope, Hospital, UsersRound,
  Clock, Calendar, MapPin, ArrowRight, Check, CheckCircle, Loader2,
  type LucideIcon,
} from 'lucide-react';
import './WorkyMarketplaceDemo.css';

enum SimulationPhase {
  DISTRIBUTION = 'DISTRIBUTION',
  PROCESSING = 'PROCESSING',
  RESULT = 'RESULT',
}

interface AgencyItem {
  readonly id: string;
  readonly name: string;
  readonly Icon: LucideIcon;
  readonly accentColor: string;
}

const AGENCY_ITEMS: readonly AgencyItem[] = [
  { id: 'ag1', name: 'Home Care Partners', Icon: Home, accentColor: '#2563eb' },
  { id: 'ag2', name: 'Nursing Care Agency', Icon: Stethoscope, accentColor: '#348d75' },
  { id: 'ag3', name: 'Community Care Team', Icon: HeartHandshake, accentColor: '#a36ce3' },
  { id: 'ag4', name: 'Senior Care Agency', Icon: UsersRound, accentColor: '#ec8a45' },
  { id: 'ag5', name: 'Healthcare Staffing', Icon: Hospital, accentColor: '#df6687' },
];
const CARE_CHECKS = [
  { label: 'Home-care experience', Icon: Heart },
  { label: 'Weekday availability', Icon: Calendar },
  { label: 'Nearby care professionals', Icon: MapPin },
] as const;
const STEPS = ['Connect', 'Match', 'Review'] as const;
const LINE_DELAYS_MS = [150, 400, 650, 900, 1150] as const;
const CHECK_DELAYS_MS = [1400, 2800, 4200] as const;
const PHASE_1_MS = 4000;
const PHASE_2_MS = 6300;
const PHASE_3_MS = 8000;
const LOOP_MS = PHASE_1_MS + PHASE_2_MS + PHASE_3_MS;
const ALL_CONNECTIONS = AGENCY_ITEMS.map(() => true);

interface ConnectionsProps {
  readonly hostRef: React.RefObject<HTMLDivElement>;
  readonly sourceRef: React.RefObject<HTMLDivElement>;
  readonly targetRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  readonly activeTargets: readonly boolean[];
  readonly incoming?: boolean;
  readonly animate?: boolean;
}

// Measure card edges so the curves stay connected when labels wrap or the layout resizes.
const FlowConnections = ({ hostRef, sourceRef, targetRefs, activeTargets, incoming = false, animate = true }: ConnectionsProps): React.ReactElement => {
  const [paths, setPaths] = useState<readonly string[]>([]);

  useEffect(() => {
    const host = hostRef.current;
    const source = sourceRef.current;
    if (!host || !source) return undefined;

    const measure = (): void => {
      const bounds = host.getBoundingClientRect();
      const origin = source.getBoundingClientRect();
      setPaths(targetRefs.current.map(target => {
        if (!target) return '';
        const destination = target.getBoundingClientRect();
        const x1 = (incoming ? destination.right : origin.right) - bounds.left;
        const y1 = (incoming ? destination.top + destination.height / 2 : origin.top + origin.height / 2) - bounds.top;
        const x2 = (incoming ? origin.left : destination.left) - bounds.left;
        const y2 = (incoming ? origin.top + origin.height / 2 : destination.top + destination.height / 2) - bounds.top;
        const middle = (x1 + x2) / 2;
        return `M ${x1} ${y1} C ${middle} ${y1}, ${middle} ${y2}, ${x2} ${y2}`;
      }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    [host, source, ...targetRefs.current].forEach(element => {
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [hostRef, sourceRef, targetRefs, incoming]);

  return (
    <svg className='wmd-flow-lines' aria-hidden='true'>
      {paths.map((path, index) => path ? (
        <g key={index}>
          <path d={path} pathLength='1' className={`wmd-flow-path ${index % 2 ? 'wmd-flow-path--sage' : ''} ${activeTargets[index] ? 'wmd-flow-path--active' : ''}`} />
          {activeTargets[index] && animate && (
            <circle r='3.5' className='wmd-flow-spark'>
              <animateMotion dur='2.8s' begin={`${index * 0.25}s`} repeatCount='indefinite' path={path} />
            </circle>
          )}
        </g>
      ) : null)}
    </svg>
  );
};

const AgencyIcon = ({ agency }: { readonly agency: AgencyItem }): React.ReactElement => (
  <span className='wmd-agency-icon' style={{ color: agency.accentColor, backgroundColor: `${agency.accentColor}0e` }}>
    <agency.Icon strokeWidth={1.8} />
  </span>
);

const DistributionPhase = ({ linesVisible, animate }: { readonly linesVisible: readonly boolean[]; readonly animate: boolean }): React.ReactElement => {
  const hostRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<HTMLDivElement>(null);
  const agencyRefs = useRef<(HTMLDivElement | null)[]>([]);
  return (
    <div className='wmd-phase wmd-phase-distrib' ref={hostRef}>
      <FlowConnections hostRef={hostRef} sourceRef={requestRef} targetRefs={agencyRefs} activeTargets={linesVisible} animate={animate} />
      <div className='wmd-request-card' ref={requestRef}>
        <span className='wmd-soft-badge'>Care request</span>
        <div className='wmd-request-title'>Personal Support Worker</div>
        <div className='wmd-request-meta'><Clock /><span>Weekday mornings</span></div>
        <div className='wmd-request-meta'><Home /><span>Toronto · Home visits</span></div>
        <div className='wmd-request-status'><span className='wmd-status-dot' /><span>Connecting with care agencies</span></div>
        <span className='wmd-request-port' />
      </div>
      <div className='wmd-agencies'>
        <div className='wmd-network-label'>Your care network</div>
        {AGENCY_ITEMS.map((agency, index) => (
          <div key={agency.id} ref={element => { agencyRefs.current[index] = element; }} className={`wmd-agency-card ${linesVisible[index] ? 'wmd-agency-card--active' : ''}`}>
            <AgencyIcon agency={agency} />
            <span>{agency.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProcessingPhase = ({ completedChecks }: { readonly completedChecks: readonly number[] }): React.ReactElement => {
  const hostRef = useRef<HTMLDivElement>(null);
  const hubRef = useRef<HTMLDivElement>(null);
  const agencyRefs = useRef<(HTMLDivElement | null)[]>([]);
  const matchRefs = useRef<(HTMLDivElement | null)[]>([]);
  const progress = completedChecks.length === CARE_CHECKS.length ? 100 : 15 + completedChecks.length * 28;
  return (
    <div className='wmd-phase wmd-phase-proc' ref={hostRef}>
      <FlowConnections hostRef={hostRef} sourceRef={hubRef} targetRefs={agencyRefs} activeTargets={ALL_CONNECTIONS} incoming />
      <FlowConnections hostRef={hostRef} sourceRef={hubRef} targetRefs={matchRefs} activeTargets={[true]} />
      <div className='wmd-network-icons'>
        {AGENCY_ITEMS.map((agency, index) => (
          <div key={agency.id} className='wmd-network-icon' ref={element => { agencyRefs.current[index] = element; }}><AgencyIcon agency={agency} /></div>
        ))}
      </div>
      <div className='wmd-worky-hub' ref={hubRef} style={{fontSize:'20px'}}>worky</div>
      <div className='wmd-match-card' ref={element => { matchRefs.current[0] = element; }}>
        <span className='wmd-soft-badge'>Finding the right fit</span>
        <div className='wmd-match-title'>Matching your care needs</div>
        <div className='wmd-match-description'>Considering experience, availability and location.</div>
        <div className='wmd-care-checks'>
          {CARE_CHECKS.map(({ label, Icon }, index) => {
            const complete = completedChecks.includes(index);
            const current = index === completedChecks.length;
            return (
              <div key={label} className='wmd-care-check'>
                <span className={`wmd-check-state ${complete ? 'wmd-check-state--complete' : current ? 'wmd-check-state--current' : ''}`}>
                  {complete ? <Check /> : current ? <Loader2 /> : <span />}
                </span>
                <span className='wmd-check-icon'><Icon /></span>
                <span>{label}</span>
              </div>
            );
          })}
        </div>
        <div className='wmd-match-progress'><span style={{ width: `${progress}%` }} /></div>
        <div className='wmd-match-status'>Preparing your shortlist…</div>
      </div>
    </div>
  );
};

const ResultPhase = (): React.ReactElement => (
  <div className='wmd-phase wmd-phase-result'>
    <div className='wmd-result-card'>
      <div className='wmd-result-header'><span className='wmd-soft-badge'><Heart />Suggested match</span><CheckCircle className='wmd-result-check' /></div>
      <div className='wmd-result-caption'>Ready for your review</div>
      <div className='wmd-result-candidate'>
        <div className='wmd-result-avatar'>
          <span>SM</span>
          <img src={`${process.env.PUBLIC_URL}/marketplace-demo/sarah-care-professional.jpg`} alt='' width='72' height='72' decoding='async' onError={event => { event.currentTarget.style.display = 'none'; }} />
        </div>
        <div className='wmd-result-candidate-info'>
          <div className='wmd-result-name'>Sarah M.</div>
          <div className='wmd-result-role'>Personal Support Worker</div>
          <div className='wmd-result-agency'><Home />Home Care Partners</div>
        </div>
      </div>
      <div className='wmd-result-details'>
        <div><HeartHandshake /><span>5 years in home care</span></div>
        <div><Calendar /><span>Weekday mornings</span></div>
        <div><Home /><span>Toronto · In-home visits</span></div>
      </div>
      {/* This is an illustrative preview; the section's existing Get Demo CTA remains the live action. */}
      <div className='wmd-result-preview-action'>Review candidate<ArrowRight /></div>
      <div className='wmd-result-note'>Your agency makes the final decision</div>
    </div>
  </div>
);

/* ── Main component ──────────────────────────────────────────────────────── */

const WorkyMarketplaceDemo = (): React.ReactElement => {
  const [phase, setPhase] = useState<SimulationPhase>(SimulationPhase.DISTRIBUTION);
  const [linesVisible, setLinesVisible] = useState<readonly boolean[]>([false, false, false, false, false]);
  const [completedChecks, setCompletedChecks] = useState<readonly number[]>([]);
  const [isInViewport, setIsInViewport] = useState<boolean>(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const loopRef = useRef<() => void>(() => { return; });

  const runSimulation = (): void => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    const schedule = (fn: () => void, delayMs: number): void => {
      const t: ReturnType<typeof setTimeout> = setTimeout(fn, delayMs);
      timersRef.current = [...timersRef.current, t];
    };

    setPhase(SimulationPhase.DISTRIBUTION);
    setLinesVisible([false, false, false, false, false]);
    setCompletedChecks([]);

    LINE_DELAYS_MS.forEach((delayMs: number, idx: number): void => {
      schedule((): void => {
        setLinesVisible((prev: readonly boolean[]): readonly boolean[] => {
          const next: boolean[] = [...prev];
          next[idx] = true;
          return next;
        });
      }, delayMs);
    });

    schedule((): void => {
      setPhase(SimulationPhase.PROCESSING);
      setCompletedChecks([]);
      CHECK_DELAYS_MS.forEach((checkDelay: number, idx: number): void => {
        schedule((): void => {
          setCompletedChecks((prev: readonly number[]): readonly number[] => [...prev, idx]);
        }, checkDelay);
      });
    }, PHASE_1_MS);

    schedule((): void => setPhase(SimulationPhase.RESULT), PHASE_1_MS + PHASE_2_MS);
    schedule((): void => loopRef.current(), LOOP_MS);
  };

  loopRef.current = runSimulation;

  useEffect((): (() => void) => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (): void => setPrefersReducedMotion(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener('change', onChange);
    return (): void => mediaQuery.removeEventListener('change', onChange);
  }, []);

  useEffect((): (() => void) | void => {
    const containerElement: HTMLDivElement | null = containerRef.current;
    if (!containerElement) return undefined;

    const observer: IntersectionObserver = new IntersectionObserver(
      (entries: readonly IntersectionObserverEntry[]): void => {
        const firstEntry: IntersectionObserverEntry | undefined = entries[0];
        setIsInViewport(Boolean(firstEntry?.isIntersecting));
      },
      {
        threshold: 0.1,
      }
    );

    observer.observe(containerElement);

    return (): void => {
      observer.disconnect();
    };
  }, []);

  useEffect((): (() => void) => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    if (!isInViewport || prefersReducedMotion) {
      setPhase(SimulationPhase.DISTRIBUTION);
      setLinesVisible([false, false, false, false, false]);
      setCompletedChecks([]);
      return (): void => { return; };
    }

    loopRef.current();

    return (): void => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [isInViewport, prefersReducedMotion]);

  const activeStep = phase === SimulationPhase.DISTRIBUTION ? 0 : phase === SimulationPhase.PROCESSING ? 1 : 2;

  return (
    <div className='wmd-container wmd-marketplace' ref={containerRef}>
      <div className='wmd-stage' role='img' aria-label='Illustrative home-care staffing flow: a Toronto personal support worker request connects to care agencies, compares experience and availability, and suggests a candidate for agency review.'>
        <div className='wmd-demo-heading' aria-hidden='true'>
          <span><span className='wmd-brand-icon'><Home /><Heart /></span>Home-care staffing</span>
          <span className='wmd-demo-example'>Illustrative example</span>
        </div>
        <div className='wmd-demo-content' aria-hidden='true'>
          {(prefersReducedMotion || phase === SimulationPhase.DISTRIBUTION) && (
            <DistributionPhase linesVisible={prefersReducedMotion ? ALL_CONNECTIONS : linesVisible} animate={!prefersReducedMotion} />
          )}
          {!prefersReducedMotion && phase === SimulationPhase.PROCESSING && <ProcessingPhase completedChecks={completedChecks} />}
          {!prefersReducedMotion && phase === SimulationPhase.RESULT && <ResultPhase />}
        </div>
        <div className='wmd-steps' aria-hidden='true'>
          {STEPS.map((step, index) => (
            <React.Fragment key={step}>
              {index > 0 && <span className='wmd-step-line' />}
              <span className={`wmd-step ${activeStep === index ? 'wmd-step--active' : ''}`}><span>{index + 1}</span>{step}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export { WorkyMarketplaceDemo };
